import { useMutation } from "@apollo/client";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Alert, Box, Button, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useAlerts } from "components/Alerts/AlertProvider";
import { IssueListButton } from "components/Forms/IssueListButton";
import { gql } from "generated-graphql";
import { FacilityWasteView, SubmissionStepType } from "generated-graphql/graphql";
import { useAdjustableStepWasteDateRange } from "hooks/useStepWasteDateRange";
import { useWasteStepper } from "hooks/useWasteStepper";
import { DateTime } from "luxon";
import { Suspense, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { TenantWasteFacilityProvider } from "routes/Customer/Waste/Facilities/Facility/TenantWasteFacilityProvider";
import {
  WasteStreamsBody,
  WasteStreamsDateRangeCaption,
  WasteStreamsInfoTooltip,
} from "routes/Customer/Waste/Facilities/Facility/WasteStreamsTile";

import { StepHeader } from "../StepHeader";
import { StepSubHeader } from "../StepSubHeader";
import { AmendInWasteOverviewLink } from "./AmendInWasteOverviewLink";
import {
  ReportableWasteCodes,
  useReportableWasteCodesRecommendation,
} from "./ReportableWasteCodes";
import { StepSaveFooter } from "./StepSaveFooter";
import { useWasteActivityCodeValidation } from "./useWasteActivityCodeValidation";
import {
  buildUpsertInput,
  describeWasteActivitySaveError,
  GeneratorStatusRecommendation,
  resolveChosenField,
  WasteActivityEdits,
} from "./wasteActivitySelections";
import { WasteGeneratorStatus } from "./WasteGeneratorStatus";

/**
 * Persists the step's chosen generator statuses / waste codes plus the live
 * recommendation shown at save time. The returned row is linked into the
 * cached `Submission.wasteActivity` (null until first save) via the fragment
 * shared with `GET_SUBMISSION_STEPPER_QUERY`.
 */
const UPSERT_SUBMISSION_WASTE_ACTIVITY = gql(`
  mutation UpsertSubmissionWasteActivity($input: UpsertSubmissionWasteActivityInput!) {
    upsertSubmissionWasteActivity(input: $input) {
      ...SubmissionWasteActivityFields
    }
  }
`);

/**
 * Compact single-column measure for the step's narrow inputs (the activity
 * date pair) so they sit on one rail down the flat layout instead of
 * stretching to the full content width.
 */
const SECTION_MAX_WIDTH = 440;

/**
 * Measure for the vertically-stacked data-entry sections (generator status,
 * waste codes). Wider than the date rail so the status selects and the
 * chip-bearing waste-code inputs have comfortable room, while still capping the
 * line length so the fields don't stretch across the whole content column.
 */
const STACKED_SECTION_MAX_WIDTH = 640;

/**
 * Stepper-native activity-window control: a start/end date pair, kept narrow with
 * a `maxWidth`. Deliberately does NOT reuse the waste-overview `WasteDateRangePicker`
 * — that component's chrome (and the dashboard tiles' cards) made the step's
 * sections read as inconsistent, nested cards. Wraps its own `LocalizationProvider`
 * so it works wherever it's mounted.
 */
function ActivityDateRange({
  startDate,
  endDate,
  onChange,
}: {
  startDate: string;
  endDate: string;
  onChange: (next: { startDate?: string; endDate?: string }) => void;
}) {
  const start = startDate ? DateTime.fromISO(startDate) : null;
  const end = endDate ? DateTime.fromISO(endDate) : null;
  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Stack direction="row" spacing={2} sx={{ maxWidth: SECTION_MAX_WIDTH }}>
        <DatePicker
          label="Start Date"
          value={start}
          format="MM/dd/yyyy"
          onChange={(next) => onChange({ startDate: next?.toISODate() ?? undefined, endDate })}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <DatePicker
          label="End Date"
          value={end}
          format="MM/dd/yyyy"
          onChange={(next) => onChange({ startDate, endDate: next?.toISODate() ?? undefined })}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </Stack>
    </LocalizationProvider>
  );
}

/** Loading placeholder for the (suspense-driven) generator status tile body. */
function GeneratorStatusSkeleton() {
  return (
    <Stack spacing={1}>
      <Skeleton height={48} variant="rounded" />
      <Skeleton height={36} variant="rounded" />
    </Stack>
  );
}

/**
 * Reportable waste streams subsection. Composes the shared chrome-free dashboard
 * grid body (fixed to the waste-streams view — the dashboard's "View by" toggle
 * is omitted here) under the step's consistent `StepSubHeader`. The enclosing
 * `TenantWasteFacilityProvider` bounds it to the activity window and the
 * `Reportable` view, so it lists only the streams included in the submission.
 *
 * The grid itself is supporting evidence for the sections above, so it stays
 * collapsed by default (including read-only/frozen submissions) behind a
 * "Show waste streams" toggle. The reveal state is local UI state only —
 * deliberately not persisted. The grid is conditionally MOUNTED (not CSS-hidden):
 * `WasteStreamsBody` suspends on a waste-line count query and the embedded data
 * grid runs its own window-scoped queries, so keeping it unmounted until
 * revealed skips that fetching for the common collapsed case.
 */
function WasteStreamsSection() {
  const [showStreams, setShowStreams] = useState(false);
  return (
    <Stack spacing={2}>
      <StepSubHeader
        title="Reportable Waste Streams"
        infoTooltip={<WasteStreamsInfoTooltip />}
        action={<WasteStreamsDateRangeCaption />}
      />
      <Typography variant="caption" color="text.secondary">
        The generator status and waste codes above are derived from the facility&apos;s waste
        streams.
      </Typography>
      {/* No aria-controls: the grid is conditionally mounted, so the referenced
          id would dangle while collapsed. */}
      <Button
        size="small"
        aria-expanded={showStreams}
        startIcon={showStreams ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setShowStreams((previous) => !previous)}
        sx={{ alignSelf: "flex-start" }}
      >
        {showStreams ? "Hide waste streams" : "Show waste streams"}
      </Button>
      {/* Skeleton height approximates the mounted grid's typical height so the
          reveal doesn't jump layout when the fallback swaps for the real grid. */}
      {showStreams && (
        <Suspense fallback={<Skeleton height={510} variant="rounded" />}>
          <WasteStreamsBody selectedGraphType="waste-streams" readOnly fitContent />
        </Suspense>
      )}
    </Stack>
  );
}

/**
 * Consolidated RCRA Waste Activity stepper step. Combines the former Generator
 * Status and Waste Streams steps into one screen, plus the submission's waste
 * codes.
 *
 * Layout: a single flat column ordered by time scope. The waste codes section
 * comes first — it is intentionally NOT bound to the activity window; its
 * recommendation is the all-time reportable set that matches the snapshot
 * fallback. A divider then separates it from the window-scoped content: the
 * user-adjustable date window (default: trailing 6 months) acts as a header
 * for the Generator Status (reported lookback + calculated status) and the
 * reportable Waste Streams grid (via `TenantWasteFacilityProvider`, collapsed
 * by default behind a "Show waste streams" toggle) it governs below.
 *
 * Generator statuses and waste codes are editable: they prefill
 * from the saved `SubmissionWasteActivity` row (or the live recommendation
 * before the first save), and Save persists chosen + recommended values via
 * `upsertSubmissionWasteActivity` before marking the step complete. The waste
 * streams grid remains read-only with a CTA out to the waste facility page,
 * which owns waste-stream edits (and destructive actions like merging).
 *
 * The live recommendations flow up from the two sections (they own the
 * queries) via `onRecommendation`, so the save payload captures exactly what
 * the user was shown; Save stays disabled until both have reported.
 */
export function WasteActivityStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const { submission, canEdit } = useWasteStepper();
  const alerts = useAlerts();
  const { startDate, endDate, setRange } = useAdjustableStepWasteDateRange();

  const saved = submission?.wasteActivity ?? null;
  const [edits, setEdits] = useState<WasteActivityEdits>({});
  const [statusRecommendation, setStatusRecommendation] =
    useState<GeneratorStatusRecommendation | null>(null);
  // The parent owns the reportable-codes query so the recommendation flows DOWN
  // to the field component as props (no child→parent effect). Save uses this
  // same value for the payload, so it's exactly what the user was shown.
  const {
    recommendation: codesRecommendation,
    loading: codesLoading,
    error: codesError,
    refetch: refetchCodes,
  } = useReportableWasteCodesRecommendation(facilityId);

  const handleEdit = useCallback((edit: WasteActivityEdits) => {
    setEdits((previous) => ({ ...previous, ...edit }));
  }, []);

  const readOnly = !canEdit;

  // Live, advisory compliance-engine validation of the CHOSEN waste codes
  // (edit → saved → recommendation, same resolution the save payload uses):
  // issues render inline at the fields and in the footer's issue list, but
  // never disable or fail Save — matching how CE issues behave on the other
  // stepper steps (Facility Profile's validating form).
  const hasSaved = saved != null;
  const chosenFederalCodes = resolveChosenField(
    edits.federalWasteCodes,
    saved?.federalWasteCodes ?? null,
    hasSaved,
    codesRecommendation?.federalWasteCodes ?? null
  );
  const chosenStateCodes = resolveChosenField(
    edits.stateWasteCodes,
    saved?.stateWasteCodes ?? null,
    hasSaved,
    codesRecommendation?.stateWasteCodes ?? null
  );
  const codeIssues = useWasteActivityCodeValidation({
    submissionId,
    federalWasteCodes: chosenFederalCodes,
    stateWasteCodes: chosenStateCodes,
    skip: readOnly,
  });

  const [upsertWasteActivity, { loading: saving }] = useMutation(UPSERT_SUBMISSION_WASTE_ACTIVITY, {
    update: (cache, { data }) => {
      const row = data?.upsertSubmissionWasteActivity;
      if (!row) {
        return;
      }
      // First save: link the new row into the cached submission so the
      // stepper shell's `wasteActivity` goes non-null without a refetch.
      cache.modify({
        id: cache.identify({ __typename: "Submission", id: submissionId }),
        fields: {
          wasteActivity: (existing, { toReference }) =>
            toReference({ __typename: "SubmissionWasteActivity", id: row.id }) ?? existing,
        },
      });
    },
  });

  const recommendation = useMemo(
    () =>
      statusRecommendation && codesRecommendation
        ? { ...statusRecommendation, ...codesRecommendation }
        : null,
    [statusRecommendation, codesRecommendation]
  );

  // Persist only — StepSaveFooter owns the step-status touch, toast, and
  // navigation. Resolves `false` when the save is rejected (frozen submission,
  // malformed codes) so the footer leaves the step status untouched.
  // Compliance-engine issues on the codes deliberately do NOT block the save;
  // they surface live at the fields via the validator query instead.
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!recommendation) {
      return false;
    }
    try {
      await upsertWasteActivity({
        variables: {
          input: buildUpsertInput({ submissionId, saved, edits, recommendation }),
        },
      });
      return true;
    } catch (error) {
      alerts.error(describeWasteActivitySaveError(error));
      return false;
    }
  }, [recommendation, upsertWasteActivity, submissionId, saved, edits, alerts]);

  return (
    <Stack spacing={3}>
      <StepHeader title="Waste Activity" />

      {/* icon={false}: design feedback — the icon eats horizontal space in
          this small inline note. */}
      <Alert severity="info" icon={false}>
        Generator status and waste codes are saved to this submission. The waste streams below are a
        read-only summary — to make changes, use the waste facility page; updates are reflected
        here. <AmendInWasteOverviewLink tenantId={tenantId} facilityId={facilityId} />
      </Alert>

      {/* All-time content first (flush, no cards): the waste codes are NOT
          scoped to the activity window, so they sit above it — everything
          below the divider is governed by the window. Capped at the stacked
          section measure so the wide inputs don't stretch the full column. */}
      <Box sx={{ maxWidth: STACKED_SECTION_MAX_WIDTH }}>
        <ReportableWasteCodes
          saved={saved}
          edits={edits}
          issues={codeIssues}
          onEdit={handleEdit}
          readOnly={readOnly}
          recommendedFederal={codesRecommendation?.federalWasteCodes ?? []}
          recommendedState={codesRecommendation?.stateWasteCodes ?? []}
          loading={codesLoading}
          error={codesError}
          onRetry={refetchCodes}
        />
      </Box>

      <Divider />

      {/* Window-scoped content: the date-range control heads the sections it
          governs (Generator Status + Reportable Waste Streams). */}
      <Stack spacing={3}>
        <Stack spacing={2}>
          <StepSubHeader title="Activity Window" />
          <Typography variant="caption" color="text.secondary">
            Governs the generator status and reportable waste streams below.
          </Typography>
          <ActivityDateRange
            startDate={startDate}
            endDate={endDate}
            onChange={(next) => {
              // Intentionally ignore cleared/partial ranges (one side missing): the
              // activity window must always stay bounded, so a reset leaves the
              // current range in place rather than unbounding the sections.
              if (next.startDate && next.endDate) {
                setRange({ startDate: next.startDate, endDate: next.endDate });
              }
            }}
          />
        </Stack>

        <Stack spacing={2} sx={{ maxWidth: STACKED_SECTION_MAX_WIDTH }}>
          <StepSubHeader title="Generator Status" />
          <Suspense fallback={<GeneratorStatusSkeleton />}>
            <WasteGeneratorStatus
              facilityId={facilityId}
              startDate={startDate}
              endDate={endDate}
              saved={saved}
              edits={edits}
              onEdit={handleEdit}
              onRecommendation={setStatusRecommendation}
              readOnly={readOnly}
            />
          </Suspense>
        </Stack>

        {/* The provider is a pure context wrapper (no queries of its own), so it
            wraps the whole section: the header's date-range caption reads the
            window from it even while the grid is collapsed. The suspending grid
            body mounts inside the section, only once revealed. */}
        <TenantWasteFacilityProvider
          facilityId={facilityId}
          startDate={startDate}
          endDate={endDate}
          view={FacilityWasteView.Reportable}
        >
          <WasteStreamsSection />
        </TenantWasteFacilityProvider>
      </Stack>

      {/* Save stays enabled regardless of code-validation issues (they are
          advisory); only a missing recommendation capture disables it. The
          footer's issue button mirrors the Facility Profile step. */}
      <StepSaveFooter
        submissionId={submissionId}
        step={SubmissionStepType.RcraWasteActivity}
        onSave={handleSave}
        saving={saving}
        disabled={recommendation == null}
        leftActions={<IssueListButton issues={codeIssues} />}
      />
    </Stack>
  );
}
